'use client'

import { Icon } from '@/components/ui/icon';
import { Bone } from '@/components/ui/bone';
import { PublicFilterParams, usePublicFilter } from '@/hooks/use-public-filter';
import { FilterConfig, FilterProps, FilterType } from '@/lib/types/ui/filter-types';
import { cn } from '@/lib/utils';
import { createInputChangeHandler } from '@/lib/utils/type-safety';
import { Button, ListBox, Select, Skeleton, Switch } from "@heroui/react";
import { useCallback, useMemo, useState } from 'react';
import { Input } from '../ui/form-elements/Input';

type FilterValue = string | number | boolean | null | undefined;

interface UnifiedFilterProps<T> extends FilterProps<T> {
  onFilterChange?: (filters: Record<string, any>) => void;
}

export function UnifiedFilter<T>({
  config,
  className = '',
  showClearButton = true,
  onFilterChange
}: UnifiedFilterProps<T>) {
  const {
    params,
    updateParams,
    clearFilters: clearAllFilters,
    isLoading
  } = usePublicFilter();

  // Simplified state for filter visibility
  const [filtersVisible, setFiltersVisible] = useState(false);

  // Simplified filters state
  const [filters, setFilters] = useState<Record<string, any>>({});

  // Simplified filter change handler
  const handleFilterChange = useCallback((key: string, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    updateParams({ [key]: value, page: 1 });
    onFilterChange?.(newFilters);
  }, [filters, onFilterChange, updateParams]);

  // Simplified clear filters handler
  const clearFilters = useCallback(() => {
    setFilters({});
    clearAllFilters();
    onFilterChange?.({});
  }, [clearAllFilters, onFilterChange]);

  // Simplified active filters check
  const hasActiveFilters = useMemo(() => {
    return Object.entries(params).some(([key, value]) => {
      if (key === 'page' || key === 'take') return false;
      return value !== undefined && value !== null && value !== '';
    });
  }, [params]);

  // Simplified filter input rendering
  const renderInput = (filter: FilterConfig<T>) => {
    const paramKey = filter.key as keyof PublicFilterParams;
    const value = params[paramKey];

    if (isLoading) {
      return <Skeleton className="w-full h-12" />;
    }

    switch (filter.type) {
      case FilterType.TEXT:
        return (
          <Input
            type="text"
            placeholder={filter.placeholder}
            value={value as string || ''}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            aria-label={filter.label || `Filter by ${filter.key}`}
            isClearable={true}
            className="w-full h-12"
            onClear={() => handleFilterChange(filter.key, undefined)}
          />
        );
      case FilterType.SELECT: {
        // Normalize the current param into a single string key for v3's
        // singular `selectedKey`. Empty/unset maps to null so the placeholder
        // shows (there is no synthetic "all" option in the collection).
        const rawSelected =
          value instanceof Set
            ? Array.from(value)[0]
            : Array.isArray(value)
              ? value[0]
              : value;
        const selectedKey =
          rawSelected === undefined || rawSelected === null || rawSelected === ''
            ? null
            : String(rawSelected);
        const options =
          filter.options?.map(opt => ({
            value: opt.value.toString(),
            label: opt.label,
          })) || [];
        return (
          <Select
            className="w-full max-w-full"
            placeholder={`Select ${filter.label.toLowerCase()}`}
            selectedKey={selectedKey}
            aria-label={filter.label || `Filter by ${filter.key}`}
            onSelectionChange={(key) => {
              // v3 single-select passes a single Key; guard the legacy
              // Set/array shapes too so behavior is unchanged for every caller.
              const selectedValue = Array.isArray(key)
                ? key[0]
                : key instanceof Set
                  ? Array.from(key)[0]
                  : key;
              handleFilterChange(filter.key, selectedValue);
            }}
            size="md"
          >
            <Select.Trigger className="h-12">
              <Select.Value className="text-default-700" />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                {options.map(opt => (
                  <ListBox.Item key={opt.value} id={opt.value} textValue={opt.label}>
                    {opt.label}
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
        );
      }
      case FilterType.CHECKBOX:
        return (
          <Switch
            isSelected={!!value}
            onChange={(isChecked) => handleFilterChange(filter.key, isChecked ? true : undefined)}
            aria-label={filter.label}
            size="sm"
            color="primary"
          >
            <Switch.Content>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              {filter.label}
            </Switch.Content>
          </Switch>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={cn(
        "mb-4 space-y-4",
        className
      )}
      role="region"
      aria-label="Filter controls"
    >
      {/* Filter toggle button */}
      <div className="flex justify-between items-center">
        <Button
          variant="ghost"
          className="flex items-center gap-2 bg-default-100"
          onPress={() => setFiltersVisible(!filtersVisible)}
          aria-expanded={filtersVisible}
          aria-controls="filter-panel"
        >
          <Icon icon="filter" className="w-4 h-4" />
          <span>Filters</span>
        </Button>

        {showClearButton && hasActiveFilters && (
          <Button
            onPress={clearFilters}
            size="sm"
            aria-label="Clear all filters"
            isDisabled={isLoading}
            variant="ghost"
          >
            <Icon icon="reset" className="text-base" aria-hidden="true" />
            Clear All
          </Button>
        )}
      </div>

      {/* Simplified filter panel */}
      {filtersVisible && (
        <div id="filter-panel" className="space-y-4 p-4 bg-content1 rounded-lg">
          {config.map(filter => {
            const inputId = `filter-${filter.key}`;
            return (
              <div key={filter.key} className="flex flex-col w-full">
                <label htmlFor={inputId} className="text-sm font-medium mb-1 text-foreground/70">
                  {filter.label}
                </label>
                <div id={inputId} className="w-full">
                  {renderInput(filter)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function UnifiedFilterSkeleton() {
  return (
    <div className="mb-4 space-y-4" role="region" aria-label="Filter controls">
      <div className="flex justify-between items-center">
        <Bone className="h-10 w-28 rounded-lg" />
        <Bone className="h-8 w-24 rounded-lg" />
      </div>
    </div>
  )
}

// Kept as a static for existing call sites; loading.tsx must use the named
// export: a dotted static resolved through a Flight client reference races
// chunk loading on client navigation and renders undefined (React #130).
UnifiedFilter.Skeleton = UnifiedFilterSkeleton;
